// src/components/layout/RightPanel/UniversalEditor.jsx
import React, { useRef, useEffect, useCallback, forwardRef, useMemo } from 'react';
import { EditorView } from '@codemirror/view';
import { EditorState, StateEffect } from '@codemirror/state';
import { createUniversalExtensions } from './UniversalEditorExtensions';
import { CONTENT_TYPES } from '@/managers/PanelManager';
import { openSearchPanel } from '@codemirror/search';

const UniversalEditor = forwardRef(({
    contentString,
    language,
    contentType: typeFromProp,
    metadata: metadataFromProp = {},
    onContentChange, // This MUST be a stable useCallback from the parent
    fontSize = 14,
    readOnly = false
}, ref) => {
    const editorRef = useRef(null); // DOM element for CodeMirror
    const viewRef = useRef(null);   // CodeMirror EditorView instance
    
    const lastAppliedConfigRef = useRef(null); 
    const lastKnownContentRef = useRef(null);

    const currentContentType = typeFromProp || CONTENT_TYPES.CODE;
    const currentContentText = typeof contentString === 'string' ? contentString : '';

    const desiredEditorConfig = useMemo(() => ({
        contentType: currentContentType,
        portalId: metadataFromProp?.portalId || metadataFromProp?.messageId || 'default',
        blockIndex: metadataFromProp?.blockIndex || 0,
        language: language || 'plaintext',
        fontSize,
        readOnly,
    }), [
        currentContentType, metadataFromProp?.portalId, metadataFromProp?.messageId,
        metadataFromProp?.blockIndex, language, fontSize, readOnly
    ]);

    const editorMetadataForExtensions = useMemo(() => ({
        ...metadataFromProp,
        language: language,
    }), [metadataFromProp, language]);

    React.useImperativeHandle(ref, () => ({
        getEditor: () => viewRef.current,
        focus: () => viewRef.current?.focus(),
        getContent: () => viewRef.current?.state.doc.toString(),
        find: () => {
            const view = viewRef.current;
            if (view) {
                openSearchPanel(view);
                return true;
            }
            return false;
        }
    }), []);

    useEffect(() => {
        if (!editorRef.current) return;
        let isMounted = true;

        const internalOnContentChange = (newText) => {
            lastKnownContentRef.current = newText;
            onContentChange(newText);
        };

        const setupOrReconfigure = async () => {
            let configHasChanged = false;
            if (!lastAppliedConfigRef.current) {
                configHasChanged = true;
            } else {
                for (const key in desiredEditorConfig) {
                    if (desiredEditorConfig[key] !== lastAppliedConfigRef.current[key]) {
                        configHasChanged = true;
                        break;
                    }
                }
            }

            if (configHasChanged || !viewRef.current) {
                const newExtensions = await createUniversalExtensions(
                    desiredEditorConfig.contentType,
                    editorMetadataForExtensions,
                    {
                        onChange: internalOnContentChange,
                        readOnly: desiredEditorConfig.readOnly,
                        fontSize: desiredEditorConfig.fontSize,
                    }
                );

                if (!isMounted) return;

                if (viewRef.current && configHasChanged) {
                    viewRef.current.dispatch({
                        effects: StateEffect.reconfigure.of(newExtensions)
                    });
                } else if (!viewRef.current) {
                    const initialState = EditorState.create({
                        doc: currentContentText, 
                        extensions: newExtensions
                    });
                    viewRef.current = new EditorView({
                        state: initialState,
                        parent: editorRef.current
                    });
                    lastKnownContentRef.current = currentContentText;
                }
                lastAppliedConfigRef.current = { ...desiredEditorConfig };
            }
        };

        setupOrReconfigure().catch(error => console.error("UniversalEditor setup/reconfigure error:", error));
        
        return () => { isMounted = false; };
    }, [desiredEditorConfig, onContentChange, editorMetadataForExtensions, currentContentText]);

    // Effect for synchronizing contentString prop with editor state
    useEffect(() => {
        const view = viewRef.current;
        if (view && typeof currentContentText === 'string' && currentContentText !== lastKnownContentRef.current) {
            const currentEditorDoc = view.state.doc.toString();
            
            // SCROLL FIX: If streaming, append content instead of replacing the whole doc.
            if (readOnly && currentContentText.startsWith(currentEditorDoc)) {
                const chunk = currentContentText.substring(currentEditorDoc.length);
                if (chunk) {
                    view.dispatch({
                        changes: { from: currentEditorDoc.length, insert: chunk },
                        userEvent: 'streaming.append'
                    });
                }
            } else if (currentEditorDoc !== currentContentText) {
                // Fallback to full replacement for non-streaming updates or resets.
                view.dispatch({
                    changes: { from: 0, to: view.state.doc.length, insert: currentContentText },
                    userEvent: 'setValue.external'
                });
            }
            lastKnownContentRef.current = currentContentText;
        }
    }, [currentContentText, readOnly]);

    // Unmount effect
    useEffect(() => {
        return () => {
            if (viewRef.current) {
                viewRef.current.destroy();
                viewRef.current = null;
            }
            lastAppliedConfigRef.current = null;
            lastKnownContentRef.current = null;
        };
    }, []);

    return (
        <div className="w-full h-full relative" ref={editorRef} />
    );
});

UniversalEditor.displayName = 'UniversalEditor';
export default UniversalEditor;